import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Container, Form } from "react-bootstrap";
import {
  CheckCircle2,
  MessageSquareText,
  RefreshCw,
  Search,
  Send,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import {
  getChatConversationAdmin,
  getChatUnreadAdmin,
  listChatConversationsAdmin,
  markChatConversationReadAdmin,
  receiveAdminChatMessage,
  sendChatMessageAdmin,
  updateAdminChatConversation,
  updateChatConversationStatusAdmin,
} from "../../actions/chatActions";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import {
  joinChatConversation,
  subscribeChatSocket,
} from "../../utils/chatSocket";
import "./AdminMessagesScreen.css";

function safeText(value = "") {
  return String(value || "").trim();
}

function getId(value = {}) {
  if (value && typeof value === "object") {
    return safeText(value.id || value._id);
  }

  return safeText(value);
}

function formatDateTime(value = "") {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

function isStaffMessage(message = {}) {
  return ["admin", "super_admin", "store_manager"].includes(
    safeText(message.senderRole).toLowerCase(),
  );
}

function getCustomerLabel(conversation = {}) {
  return (
    safeText(conversation.firstName) ||
    safeText(conversation.customerName) ||
    safeText(conversation.customerEmail) ||
    "Customer"
  );
}

function formatEnquiryType(value = "") {
  const text = safeText(value).replace(/_/g, " ");

  if (!text) return "";

  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getVerificationLabel(conversation = {}) {
  return conversation.emailVerified ? "Verified email" : "Unverified";
}

export default function AdminMessagesScreen() {
  const dispatch = useDispatch();
  const messageEndRef = useRef(null);

  const {
    loading = false,
    detailsLoading = false,
    sending = false,
    statusUpdating = false,
    conversations = [],
    activeConversation = null,
    activeMessages = [],
    unread = {},
    statusFilter = "open",
    error = null,
    detailsError = null,
  } = useSelector((state) => state.adminChat || {});

  const [filter, setFilter] = useState("open");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState("");
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState("");

  const activeConversationId = getId(activeConversation);
  const selectedConversation = useMemo(() => {
    const activeMatches = activeConversationId === activeId;

    return (
      conversations.find((conversation) => getId(conversation) === activeId) ||
      (activeMatches ? activeConversation : null) ||
      null
    );
  }, [activeConversation, activeConversationId, activeId, conversations]);

  const loadConversations = useCallback(
    ({ nextFilter = filter, nextSearch = search } = {}) => {
      return dispatch(
        listChatConversationsAdmin({
          status: nextFilter,
          search: nextSearch,
          limit: 100,
        }),
      ).catch(() => {});
    },
    [dispatch, filter, search],
  );

  useEffect(() => {
    loadConversations();
    dispatch(getChatUnreadAdmin()).catch(() => {});
  }, [dispatch, loadConversations]);

  useEffect(() => {
    if (activeId || conversations.length === 0) return;

    setActiveId(getId(conversations[0]));
  }, [activeId, conversations]);

  useEffect(() => {
    if (!activeId) return;

    dispatch(getChatConversationAdmin(activeId))
      .then(() => dispatch(markChatConversationReadAdmin(activeId)))
      .catch(() => {});

    joinChatConversation(activeId).catch(() => {});
  }, [activeId, dispatch]);

  useEffect(() => {
    return subscribeChatSocket({
      onMessage: (payload) => {
        dispatch(receiveAdminChatMessage(payload));

        if (getId(payload?.conversation) === activeId) {
          dispatch(markChatConversationReadAdmin(activeId)).catch(() => {});
        }
      },
      onConversationUpdate: (payload) => {
        dispatch(updateAdminChatConversation(payload));
      },
    });
  }, [activeId, dispatch]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeMessages, activeConversationId]);

  const handleSearch = (event) => {
    event.preventDefault();
    const nextSearch = safeText(searchInput);

    setSearch(nextSearch);
    loadConversations({ nextSearch });
  };

  const handleFilterChange = (nextFilter) => {
    setFilter(nextFilter);
    setActiveId("");
    loadConversations({ nextFilter });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const safeMessage = safeText(draft);

    if (!activeId || !safeMessage) return;

    setLocalError("");

    try {
      await dispatch(sendChatMessageAdmin(activeId, safeMessage));
      setDraft("");
    } catch (submitError) {
      setLocalError(
        submitError?.normalizedMessage ||
          submitError?.message ||
          "We could not send that reply.",
      );
    }
  };

  const handleToggleStatus = async () => {
    if (!activeId || !activeConversation) return;

    const currentStatus = safeText(activeConversation.status).toLowerCase();
    const nextStatus = currentStatus === "closed" ? "open" : "closed";

    await dispatch(updateChatConversationStatusAdmin(activeId, nextStatus));
    await loadConversations();
  };

  return (
    <Container fluid className="admin-messages-screen py-4">
      <div className="admin-messages-header">
        <div>
          <h1>Messages</h1>
          <div className="admin-messages-subtitle">
            Customer chats and product enquiries
          </div>
        </div>

        <div className="admin-messages-header-actions">
          <Badge bg={Number(unread.messages || 0) > 0 ? "danger" : "secondary"}>
            {Number(unread.messages || 0)} unread
          </Badge>
          <Button
            type="button"
            variant="outline-dark"
            onClick={() => loadConversations()}
            disabled={loading}
          >
            <RefreshCw size={17} strokeWidth={1.8} aria-hidden="true" />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      {error ? <Message variant="danger">{error}</Message> : null}

      <div className="admin-messages-layout">
        <aside className="admin-messages-sidebar" aria-label="Conversations">
          <div className="admin-messages-filters">
            <div className="admin-messages-segmented" role="group">
              {["open", "closed", "all"].map((item) => (
                <button
                  key={item}
                  type="button"
                  className={filter === item ? "is-active" : ""}
                  onClick={() => handleFilterChange(item)}
                >
                  {item}
                </button>
              ))}
            </div>

            <Form className="admin-messages-search" onSubmit={handleSearch}>
              <Form.Control
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search"
                aria-label="Search conversations"
              />
              <Button type="submit" variant="dark" aria-label="Search">
                <Search size={17} strokeWidth={1.8} />
              </Button>
            </Form>
          </div>

          <div className="admin-messages-list">
            {loading ? <Loader /> : null}

            {!loading && conversations.length === 0 ? (
              <div className="admin-messages-empty-list">
                No {statusFilter} conversations.
              </div>
            ) : null}

            {conversations.map((conversation) => {
              const conversationId = getId(conversation);
              const unreadStaffCount = Number(
                conversation.unreadStaffCount || 0,
              );

              return (
                <button
                  type="button"
                  key={conversationId}
                  className={`admin-message-row${
                    conversationId === activeId ? " is-active" : ""
                  }`}
                  onClick={() => setActiveId(conversationId)}
                >
                  <span className="admin-message-row-main">
                    <span className="admin-message-row-title">
                      {getCustomerLabel(conversation)}
                    </span>
                    <span className="admin-message-row-badges">
                      <span
                        className={`admin-message-row-status${
                          conversation.emailVerified ? " is-verified" : ""
                        }`}
                      >
                        {getVerificationLabel(conversation)}
                      </span>
                      {conversation.enquiryType ? (
                        <span className="admin-message-row-status">
                          {formatEnquiryType(conversation.enquiryType)}
                        </span>
                      ) : null}
                    </span>
                    <span className="admin-message-row-preview">
                      {conversation.lastMessagePreview || "No messages yet"}
                    </span>
                    {conversation.product?.productName ? (
                      <span className="admin-message-row-product">
                        {conversation.product.productName}
                      </span>
                    ) : null}
                  </span>

                  <span className="admin-message-row-meta">
                    {unreadStaffCount > 0 ? (
                      <span className="admin-message-row-badge">
                        {unreadStaffCount}
                      </span>
                    ) : null}
                    <span>{formatDateTime(conversation.lastMessageAt)}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section className="admin-messages-thread" aria-label="Selected chat">
          {activeId && selectedConversation ? (
            <>
              <header className="admin-thread-header">
                <div>
                  <div className="admin-thread-customer">
                    <MessageSquareText size={20} strokeWidth={1.8} />
                    <span>{getCustomerLabel(selectedConversation)}</span>
                    <span
                      className={`admin-thread-verify${
                        selectedConversation.emailVerified ? " is-verified" : ""
                      }`}
                    >
                      {getVerificationLabel(selectedConversation)}
                    </span>
                  </div>
                  <div className="admin-thread-contact">
                    {selectedConversation.customerEmail || "No email provided"}
                    {selectedConversation.product?.productName
                      ? ` | ${selectedConversation.product.productName}`
                      : ""}
                  </div>
                  <div className="admin-thread-facts">
                    {selectedConversation.enquiryType ? (
                      <span>
                        {formatEnquiryType(selectedConversation.enquiryType)}
                      </span>
                    ) : null}
                    {formatDateTime(selectedConversation.createdAt) ? (
                      <span>
                        Started {formatDateTime(selectedConversation.createdAt)}
                      </span>
                    ) : null}
                    {formatDateTime(selectedConversation.lastMessageAt) ? (
                      <span>
                        Last message{" "}
                        {formatDateTime(selectedConversation.lastMessageAt)}
                      </span>
                    ) : null}
                  </div>
                </div>

                <Button
                  type="button"
                  variant={
                    activeConversation?.status === "closed"
                      ? "outline-dark"
                      : "dark"
                  }
                  onClick={handleToggleStatus}
                  disabled={statusUpdating}
                >
                  <CheckCircle2
                    size={17}
                    strokeWidth={1.8}
                    aria-hidden="true"
                  />
                  <span>
                    {activeConversation?.status === "closed"
                      ? "Reopen"
                      : "Close"}
                  </span>
                </Button>
              </header>

              {detailsError ? (
                <Message variant="danger">{detailsError}</Message>
              ) : null}

              <div className="admin-thread-messages">
                {detailsLoading ? <Loader /> : null}

                {!detailsLoading && activeMessages.length === 0 ? (
                  <div className="admin-thread-empty">No messages yet.</div>
                ) : null}

                {activeMessages.map((message) => {
                  const staff = isStaffMessage(message);

                  return (
                    <article
                      key={getId(message)}
                      className={`admin-thread-message${
                        staff ? " from-staff" : " from-customer"
                      }`}
                    >
                      <div className="admin-thread-message-meta">
                        <strong>{staff ? "Staff" : message.senderName}</strong>
                        {formatDateTime(message.createdAt)
                          ? ` · ${formatDateTime(message.createdAt)}`
                          : ""}
                      </div>
                      <div className="admin-thread-message-text">
                        {message.message}
                      </div>
                    </article>
                  );
                })}
                <div ref={messageEndRef} />
              </div>

              <Form className="admin-thread-compose" onSubmit={handleSubmit}>
                {localError ? (
                  <Message variant="danger">{localError}</Message>
                ) : null}

                <Form.Control
                  as="textarea"
                  rows={3}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  placeholder="Reply"
                  maxLength={2000}
                  disabled={activeConversation?.status === "closed"}
                />
                <Button
                  type="submit"
                  variant="dark"
                  disabled={
                    sending ||
                    !safeText(draft) ||
                    activeConversation?.status === "closed"
                  }
                >
                  <Send size={17} strokeWidth={1.8} aria-hidden="true" />
                  <span>{sending ? "Sending" : "Send"}</span>
                </Button>
              </Form>
            </>
          ) : (
            <div className="admin-thread-placeholder">
              Select a conversation.
            </div>
          )}
        </section>
      </div>
    </Container>
  );
}
